import { useCardReader } from "application/contexts/cardReader/CardReaderContext";
import { useCatalogStore } from "application/contexts/catalog/CatalogStore";
import { useIdle } from "application/contexts/idle/IdleContext";
import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import { useSession } from "application/contexts/session/SessionContext";
import { useUi } from "application/contexts/ui/UIContext";
import Toolbar from "areas/_catalog/components/toolbar/Toolbar";
import InputText from "components/inputText/InputText";
import { IOrderProviderRef } from "modules/order/presentation/OrderProvider";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import totemGif from "../../../assets/images/totem.gif";
import styles from "./NominalCashless.module.scss";
import { useTranslation } from "i18n/useTranslation";
import { isNullOrEmpty } from "utils/utils";
import { ReactNativeFacial } from "Infrastructure/services/reactNativeServices/ReactNativeFacial";
import { Button } from "@material-ui/core";
import { useCardReaderStore } from "modules/cardReader/infra/store/CardReaderStore";
import { useShallow } from "zustand/react/shallow";
import { CardDataSource, ICardReaderStore } from "modules/cardReader/domain/interfaces/IUseCardReaderStore";
import { FacialErrorToast } from "components/facialErrorToast/FacialErrorToast";
import { SimilarFaceSelection } from "components/similarFaceSelection";
import type { SimilarFace } from "modules/reactNativeBridge/FacialReactNativeBridge";
import type { FacialUserData } from "modules/reactNativeBridge/FacialReactNativeBridge";

export interface INominalCashless {
  orderProvider: React.RefObject<IOrderProviderRef>;
}

const NominalCashless: FC<INominalCashless> = ({ orderProvider }) => {
  const { kiosk } = useKiosk();
  const history = useHistory();
  const { replace } = history;
  const { toast } = useUi();
  const { verifySessionIsOpen } = useSession();
  const { resolution } = useScreenStore();
  const { t } = useTranslation();
  const { handleActiveDefaultIdleTimer, handleDisableIdleTimer } = useIdle();

  const {
    getNominalCardDataWithName,
    enableSensor,
    disableSensor,
    clearCardData,
  } = useCardReader();


  const [cardData, groupDiscounts, setCardDataSource] = useCardReaderStore(useShallow((state: ICardReaderStore) => [
    state.cardData,
    state.groupDiscounts,
    state.setCardDataSource,
  ]));
  const { handleRestrictions, catalog, handleDiscounts } = useCatalogStore();

  const [nickname, setNickname] = useState<string>("");
  const [isFacialLoading, setIsFacialLoading] = useState<boolean>(false);
  const [showFacialError, setShowFacialError] = useState<boolean>(false);
  const [similarFacesToSelect, setSimilarFacesToSelect] = useState<SimilarFace[] | null>(null);

  const facialService = ReactNativeFacial();

  const { showLoading, hideLoading } = useUi();


  useEffect(() => {
    enableSensor(true);
    return () => {
      disableSensor();
    };
  }, [enableSensor, disableSensor]);

  useEffect(() => {
    if (isFacialLoading) {
      handleDisableIdleTimer();
    } else {
      handleActiveDefaultIdleTimer();
    }
  }, [isFacialLoading, handleActiveDefaultIdleTimer, handleDisableIdleTimer]);

  useEffect(() => {
    const verifyRestrictions = async () => {
      //Validação de restrições de catalogo para Meep Escola
      if (cardData) {
        console.log("cardData restrictions", cardData.restrictions);
        try {
          showLoading();
          if (
            isNullOrEmpty(cardData.nickname) ||
            isNullOrEmpty(cardData.password)
          ) {
            toast(t("cashless.messages.invalid_nominal_card"), "error");
            clearCardData();
            enableSensor(true);
            return;
          }
          handleRestrictions(cardData.restrictions ?? []);
          handleDiscounts(cardData.group ? groupDiscounts ?? [] : []);
          //const hasOpenSession = await verifySessionIsOpen();//TODO: Verificar se a sessão está aberta
          if (!history.location.pathname.startsWith("/nominalCashless")) {
            return;
          }
          if (catalog?.stores) {
            if (catalog.stores.length === 1) {
              replace("/catalog/store/" + 0);
            } else {
              replace("/catalog/");
            }
          }
        } catch (error) {
          console.error("Erro ao verificar restrições", error);
        } finally {
          hideLoading();
        }
      }
    };
    verifyRestrictions();
  }, [cardData, catalog, groupDiscounts, handleDiscounts, handleRestrictions, history, replace, verifySessionIsOpen, toast, t, clearCardData, enableSensor, showLoading, hideLoading]);

  const getCardDataWithName = useCallback(() => {
    if (nickname.length >= 3) {
      getNominalCardDataWithName(nickname);
      setCardDataSource(CardDataSource.NickName);
    } else {
      toast(t("cashless.nickname.invalid"), "error");
    }
  }, [setCardDataSource, nickname, getNominalCardDataWithName, toast, t]);

  const handleFacialRecognition = useCallback(async () => {
    if (!kiosk?.localId) {
      toast("Erro ao obter configuração do local", "error");
      return;
    }

    setIsFacialLoading(true);
    showLoading();
    disableSensor(); // Desativa o sensor para evitar timeout

    try {
      const response = await facialService.captureAndIdentify({
        personGroupId: kiosk.localId,
        autoCapture: true,
      });

      if (!response.success) {
        hideLoading();
        setShowFacialError(true);
        setIsFacialLoading(false);
        return;
      }

      if (!response.isIdentified && !response.similarFaces?.length) {
        hideLoading();
        setShowFacialError(true);
        setIsFacialLoading(false);
        return;
      }

      if (response.similarFaces && response.similarFaces.length >= 2) {
        hideLoading();
        setSimilarFacesToSelect(response.similarFaces);
        setIsFacialLoading(false);
        return;
      }

      const userData = response.userData ?? (response.similarFaces?.[0]
        ? (JSON.parse(response.similarFaces[0].userData) as FacialUserData)
        : null);

      if (!userData) {
        hideLoading();
        setShowFacialError(true);
        setIsFacialLoading(false);
        return;
      }

      setCardDataSource(CardDataSource.FacialRecognition);

      if (userData.nickname) {
        await getNominalCardDataWithName(userData.nickname);
      } else {
        hideLoading();
        setShowFacialError(true);
        setIsFacialLoading(false);
        return;
      }

    } catch (error) {
      hideLoading();
      setShowFacialError(true);
      console.error("Erro no reconhecimento facial", error);
    } finally {
      setIsFacialLoading(false);
      hideLoading();
    }
  }, [kiosk, showLoading, disableSensor, toast, facialService, setCardDataSource, getNominalCardDataWithName, hideLoading]);

  const handleSimilarFaceSelect = useCallback(
    async (face: SimilarFace) => {
      try {
        const userData = JSON.parse(face.userData) as FacialUserData;
        setSimilarFacesToSelect(null);
        setCardDataSource(CardDataSource.FacialRecognitionSimilarFace);
        if (userData.nickname) {
          showLoading();
          await getNominalCardDataWithName(userData.nickname);
        } else {
          setShowFacialError(true);
        }
      } catch {
        setShowFacialError(true);
      } finally {
        hideLoading();
        setIsFacialLoading(false);
      }
    },
    [setCardDataSource, getNominalCardDataWithName, showLoading, hideLoading]
  );

  const handleSimilarFaceCancel = useCallback(() => {
    setSimilarFacesToSelect(null);
    enableSensor(true);
    setIsFacialLoading(false);
  }, [enableSensor]);

  /**
     const faceIdEnabledLocalIds = [
      "c212c9e3-625c-f9b5-b6f3-42856b64446b", // Edu Cafe - Escola Integrada Educativa
      "bdddefaf-164d-95cf-38b3-e055029e6eae", // Colégio Trilingue - SC
      "c60b0b28-76d3-d9a1-2912-7ea58caafb58", // Comercial BH
      "d4ef721a-3d20-52a3-45f3-feb4ecc6cfdc", // Ramon Rodrigues - teste
      "fbc1eef7-4fb7-56c6-2e7c-b7659793306a", // Colégio Alfa Francisco Beltrão
      "1ef941f7-4e67-f1f4-5710-f84b876314c3", // Colégio Alfa Chapecó
      "95662435-0e25-d681-e8ce-747f07b24ae2", // Colégio Monteiro Anglo
      "75168ee5-f93c-cfe2-b3ba-0f47f6a71045", // São Francisco
      "2ac8f149-9de6-d67c-ef8b-7c2f07af507e", // Concórdia
      "b3b9427f-eefb-860e-99fe-e0e1e9be5543", // Pampulha
      "54aa319b-7424-481c-e6c7-b3fccc9f2265", // Eldorado
      "e64aa87e-9e0b-561d-6e66-99c27a594600", // Colégio Dom Ferraz - Caxambu
      "93e5834a-24d2-4fdb-26b0-e4297eec5ec3", // Colégio Dom Ferraz Solar - São Lourenço
    ];
   */

  const enableFacialRecognition = Boolean(kiosk?.shouldUseFacialRecognition);


  const handleRetryFacialRecognition = () => {
    setShowFacialError(false);
    handleFacialRecognition();
  };

  const handleBackFromFacialError = () => {
    setShowFacialError(false);
    enableSensor(true);
  };

  return (
    <div id={styles.NominalCashless}>
      <div className={styles.readCard}>
        <Toolbar hideWelcome title={kiosk?.localName ?? ""} />
        <div className={styles.content}>
          <span
            style={{
              fontSize: resolution === ResolutionType.FULL_HD ? 45 : 30,
            }}
          >
            {t("cashless.read_card_instruction")}
          </span>
          <img
            src={totemGif}
            alt="Animação"
            style={{
              width: resolution === ResolutionType.FULL_HD ? 1000 : 700,
              height: resolution === ResolutionType.FULL_HD ? 1000 : 700,
            }}
          />
          {kiosk?.paymentNominalCashless && kiosk?.searchMeepCardByName && (
            <div className={styles.nicknameContainer}>

              {enableFacialRecognition && <Button
                variant="contained"
                onClick={handleFacialRecognition}
                disabled={isFacialLoading}
                className={styles.facialButton}
                style={{
                  fontSize: resolution === ResolutionType.FULL_HD ? 60 : 40,
                  marginTop: 20,
                  padding: "20px 40px",
                  height: "120px",
                  width: "100%",
                  gap: 20,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/*<FaceOutlined style={{ fontSize: resolution === ResolutionType.FULL_HD ? 60 : 40 }} />*/}
                {isFacialLoading
                  ? "Reconhecendo..."
                  : "Entrar com Reconhecimento Facial"}
              </Button>}
              <InputText
                label={t("cashless.nickname.label")}
                placeHolder={t("cashless.nickname.placeholder")}
                value={nickname}
                type={"text"}
                onChange={(value: string) => setNickname(value)}
                onBlur={() => setNickname("")}
                onPressEnter={getCardDataWithName}
              />
            </div>
          )}
        </div>
      </div>

      <SimilarFaceSelection
        isOpen={similarFacesToSelect != null && similarFacesToSelect.length >= 2}
        similarFaces={similarFacesToSelect ?? []}
        onSelect={handleSimilarFaceSelect}
        onCancel={handleSimilarFaceCancel}
      />
      <FacialErrorToast
        isOpen={showFacialError}
        onRetry={handleRetryFacialRecognition}
        onBack={handleBackFromFacialError}
        onClose={handleBackFromFacialError}
      />
    </div>
  );
};

export default NominalCashless;
